import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useT } from "../context/LanguageContext";
import { ListenButton } from "./ListenButton";
import { AudioNote } from "./AudioNote";
import { MessagesSquare, ChevronDown, ShieldCheck } from "lucide-react";

// Public, anonymous view of general enquiry threads an admin has published.
export function CommunityQA() {
  const [threads, setThreads] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [msgs, setMsgs] = useState({});
  const { t: tr } = useT();

  useEffect(() => { api.get("/public/enquiries").then(({ data }) => setThreads(data)).catch(() => setThreads([])); }, []);

  const toggle = async (gid) => {
    if (openId === gid) { setOpenId(null); return; }
    setOpenId(gid);
    if (!msgs[gid]) {
      try { const { data } = await api.get(`/public/enquiries/${gid}`); setMsgs((m) => ({ ...m, [gid]: data.messages })); } catch { /* ignore */ }
    }
  };

  if (threads.length === 0) return null;

  return (
    <section className="mt-16" data-testid="community-qa">
      <h2 className="font-heading font-bold text-3xl tracking-tight text-ink flex items-center gap-3">
        <MessagesSquare className="w-7 h-7 text-clinical" /> {tr("Community Questions")}
      </h2>
      <p className="text-muted2 mt-2 mb-8">{tr("Answers to questions from the community, shared anonymously by our health team.")}</p>
      <div className="grid gap-3">
        {threads.map((t) => (
          <div key={t.id} data-testid="community-qa-item" className="bg-white border border-line rounded-xl overflow-hidden">
            <button onClick={() => toggle(t.id)} data-testid="community-qa-toggle" className="w-full text-left p-4 flex items-start justify-between gap-3 hover:bg-clinical/5 transition-colors">
              <div>
                <p className="font-heading font-bold text-ink">{tr(t.title)}</p>
                <p className="text-sm text-muted2 mt-1 line-clamp-2">{tr(t.preview)}</p>
              </div>
              <ChevronDown className={`w-5 h-5 text-muted2 shrink-0 transition-transform ${openId === t.id ? "rotate-180" : ""}`} />
            </button>
            {openId === t.id && (
              <div className="px-4 pb-4 flex flex-col gap-2" data-testid="community-qa-thread">
                {(msgs[t.id] || []).map((m, i) => (
                  <div key={i} className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${m.from_public ? "bg-sand self-start" : "bg-clinical text-white self-end"}`}>
                    <div className="text-[10px] uppercase font-bold opacity-70 mb-0.5 flex items-center gap-1">
                      {!m.from_public && <ShieldCheck className="w-3 h-3" />} {m.from_name}
                    </div>
                    {tr(m.body)}
                    {!m.from_public && m.audio_url && <AudioNote src={m.audio_url} label="Voice reply" testid="community-qa-audio" />}
                    {!m.from_public && !m.audio_url && <div className="mt-1"><ListenButton text={m.body} testid="community-qa-listen-btn" className="text-white/90 hover:text-white" /></div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
